import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;

    const poll = async (tries = 0) => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setAttempts(tries);
        setAmount(data.amount || 0);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
        if (tries >= 20) { setStatus("timeout"); return; }
        timerRef.current = setTimeout(() => poll(tries + 1), 2000);
      } catch { if (!cancelled) setStatus("error"); }
    };
    poll(0);
    return () => { cancelled = true; clearTimeout(timerRef.current); };
  }, [sessionId]);

  const paid = status === "paid";
  return (
    <div className="min-h-screen bg-[#FAF7F2] grid place-content-center p-6">
      <div className="max-w-md bg-white rounded-3xl border border-[#E5DCCE] p-8 text-center shadow-sm">
        <div className={`w-16 h-16 rounded-full grid place-content-center mx-auto ${
          paid ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-700"
        }`}>
          {paid ? <CheckCircle2 className="w-8 h-8"/> : <Sparkles className="w-8 h-8 animate-pulse"/>}
        </div>
        <h1 className="font-display text-3xl font-bold mt-4" data-testid="pay-success-title">
          {status === "checking" && "Confirming payment…"}
          {status === "paid" && "Payment received!"}
          {status === "failed" && "Payment failed"}
          {status === "timeout" && "Still processing…"}
          {status === "error" && "Something went wrong"}
        </h1>
        <p className="text-sm text-[#6B625B] mt-2">
          {status === "checking" && `Give us a moment (attempt ${attempts + 1}/20)`}
          {status === "paid" && `Thanks — $${amount?.toFixed(2)} received. Your groomer has been notified.`}
          {status === "failed" && "The payment couldn't be completed. Please try again."}
          {status === "timeout" && "Your bank is still confirming. We'll update your dashboard shortly."}
          {status === "error" && "We couldn't verify the payment. Please refresh."}
        </p>
        <Button onClick={() => nav("/client")}
          className="mt-6 rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile"
          data-testid="btn-pay-home">
          Back to home
        </Button>
      </div>
    </div>
  );
}
